import {
  PORTAL_MOBILE_ONLY_NOTICE,
  classifyPrimaryAction,
  primaryActionContract,
  type PortalExecutableCommand,
  type UxPrimaryActionKind,
} from '../../shared/ux/index.ts';

export type PortalPrimaryActionMode = 'capture' | 'navigate-proof' | 'command' | 'mobile-only' | 'wait';

export type PortalWorkspacePrimaryActions = {
  capture: { href: string; label: string } | null;
  proof: { href: string; label: 'View Proof'; prominence: 'primary' | 'secondary' } | null;
  command: { action: PortalExecutableCommand; label: string } | null;
  mobileOnly: { label: string } | null;
  waiting: boolean;
};

export function portalPrimaryActionMode(kind: UxPrimaryActionKind): Exclude<PortalPrimaryActionMode, 'wait'> {
  const classified = classifyPrimaryAction(kind);
  if (classified.portalMode === 'NATIVE_HANDOFF') return 'capture';
  if (classified.portalMode === 'NAVIGATION') return 'navigate-proof';
  if (classified.portalMode === 'COMMAND') return 'command';
  if (classified.portalMode === 'MOBILE_ONLY') return 'mobile-only';
  const exhaustive: never = classified.portalMode;
  return exhaustive;
}

function executablePortalCommand(kind: UxPrimaryActionKind): PortalExecutableCommand {
  const portal = primaryActionContract(kind).portal;
  if (portal.mode !== 'COMMAND') {
    throw new Error(`PackProof portal has no executor for ${kind}.`);
  }
  return portal.command;
}

export function portalWorkspacePrimaryActions(input: {
  transactionId: string;
  primaryAction: { kind: UxPrimaryActionKind; label: string } | null;
  proofAvailable: boolean;
}): PortalWorkspacePrimaryActions {
  const kind = input.primaryAction?.kind ?? null;
  const mode = kind ? portalPrimaryActionMode(kind) : 'wait';
  const capture = mode === 'capture' && input.primaryAction
    ? {
        href: `/packproofs/${input.transactionId}/handoff`,
        label: 'Continue on phone',
      }
    : null;
  const proof = input.proofAvailable
    ? {
        href: `/packproofs/${input.transactionId}/proof`,
        label: 'View Proof' as const,
        prominence: (capture || mode === 'command' ? 'secondary' : 'primary') as 'primary' | 'secondary',
      }
    : null;
  const command = mode === 'command' && input.primaryAction
    ? {
        action: executablePortalCommand(input.primaryAction.kind),
        label: input.primaryAction.label,
      }
    : null;
  const mobileOnly = mode === 'mobile-only'
    ? { label: PORTAL_MOBILE_ONLY_NOTICE }
    : null;

  return {
    capture,
    proof,
    command,
    mobileOnly,
    waiting: mode === 'wait',
  };
}

export function createPortalCommandLock() {
  let pending = false;
  return {
    get pending() {
      return pending;
    },
    async run(task: () => Promise<void>): Promise<'ran' | 'ignored'> {
      if (pending) return 'ignored';
      pending = true;
      try {
        await task();
        return 'ran';
      } finally {
        pending = false;
      }
    },
  };
}

export async function clickPortalConfirmTerms(input: {
  lock: ReturnType<typeof createPortalCommandLock>;
  transactionId: string;
  confirmTerms: (transactionId: string) => Promise<unknown>;
  reloadAuthoritativeWorkspace: () => Promise<void>;
  setPending: (pending: boolean) => void;
  setError: (message: string | null) => void;
}): Promise<'ran' | 'ignored'> {
  return input.lock.run(async () => {
    input.setPending(true);
    input.setError(null);
    try {
      await input.confirmTerms(input.transactionId);
      await input.reloadAuthoritativeWorkspace();
    } catch (caught: unknown) {
      input.setError(caught instanceof Error ? caught.message : 'Could not confirm these terms.');
    } finally {
      input.setPending(false);
    }
  });
}
